import React, {useState} from 'react';
import LargeModal, {LargeModalProps} from '../LargeModal';
import {useForm} from 'antd/es/form/Form';
import ProForm, {ProFormProps} from '../../../ProForm';
import {FormInstance, Modal, Spin} from 'antd';
import useRequestFn from '../../../../Utils/Hooks/useRequestFn';
import classNames from 'classnames';
import styles from './index.module.scss';

type ModalProps = Omit<LargeModalProps, 'onOk'>;

type FormProps<T> = Omit<
    ProFormProps<T>,
    'className' | 'form' | 'onFinish' | 'initialValues' | 'disabled'
>;

export interface FormModalProps<T> extends ModalProps {
    className?: string;
    formClassName?: string;
    onFinish: (value: T) => Promise<any>;
    initialValues?: Partial<T>;
    disabled?: boolean;
    formProps?: FormProps<T>;
    children: React.ReactNode;
    size?: 'large' | 'normal';
    form?: FormInstance<T>;
    loading?: boolean;
}

function FormModal<T>({
    open,
    className,
    formClassName,
    formProps,
    onFinish,
    onCancel,
    initialValues,
    disabled,
    children,
    size,
    form,
    loading,
    okButtonProps,
    ...extraProps
}: FormModalProps<T>) {
    const {onValuesChange, ...extraFormProps} = formProps || {};

    const [touched, setTouched] = useState(false);
    const [callOnFinish, submitState] = useRequestFn(onFinish);
    const [innerForm] = useForm<T>(form);

    async function onSubmit(data: T) {
        await callOnFinish(data);
    }

    function handleCancel(e: React.MouseEvent<HTMLButtonElement>) {
        if (touched) {
            Modal.confirm({
                content: '信息未保存，确定关闭页面吗？',
                onOk: () => onCancel?.(e),
            });
        } else {
            onCancel?.(e);
        }
    }

    // 表单变更时修改touched树形
    function handleValuesChange(changedValues: any, values: T) {
        setTouched(innerForm.isFieldsTouched());
        onValuesChange?.(changedValues, values);
    }

    const ModalComponent = size === 'large' ? LargeModal : Modal;

    return (
        <ModalComponent
            className={className}
            okText="保存"
            cancelText="关闭"
            onOk={innerForm.submit}
            confirmLoading={submitState.loading}
            open={open}
            onCancel={handleCancel}
            okButtonProps={{
                disabled: loading || !touched,
                ...okButtonProps,
            }}
            {...extraProps}>
            <Spin spinning={loading}>
                <ProForm
                    form={innerForm}
                    className={classNames(formClassName, {
                        [styles.noTouch]: submitState.loading,
                    })}
                    onFinish={onSubmit}
                    onValuesChange={handleValuesChange}
                    initialValues={initialValues}
                    disabled={disabled}
                    {...extraFormProps}>
                    {children}
                </ProForm>
            </Spin>
        </ModalComponent>
    );
}

FormModal.defaultProps = {
    maskClosable: false,
    loading: false,
};

export default FormModal;
